import './App.css';
import {
  AUTHENTICATION_TIMEOUT,
  LOGIN_TIMEOUT,
  TOKEN_RENEWAL_TIME,
  NUMBER_ARRAY,
  DASHBOARD_DATA_ACCEPTABLE_STATUSES,
} from 'common/global/constants';
import { BlueTheme, RedTheme } from 'common/global';
import React, { StrictMode, useEffect, useState } from 'react';
import { loginRequest, msalConfig } from './common/authentication/authConfig';
import { useSelector } from 'react-redux';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import CssBaseline from '@mui/material/CssBaseline';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { LogoutView } from 'common/pages/logoutView';
import { RouterConfig } from 'common/navigation';
import { ThemeProvider } from '@mui/material/styles';
import { logError } from 'TAP/utils/commonMethods';
import { InActiveStatusPopup } from 'TAP/components/InActiveStatusPopup';
import { TapGlobalMessages } from 'TAP/global';
import { TransferUniversalDashboardData } from 'TAP/redux/actions';
import { getUserAgentBrowser, getUserAgentOS } from 'TAP/utils/commonMethods';
import { useDispatch } from 'react-redux';

function App() {
  const theme = [RedTheme, BlueTheme];
  let setThemeInState = useSelector((state) => {
    return state.themeSwitcherRd;
  });
  const themeSelected = setThemeInState?.selectedTheme?.themeType
    ? theme[setThemeInState.selectedTheme.themeType]
    : theme[0];

  let { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [showInactivityPopup, setShowInactivityPopup] = useState(false);
  const [showTokenExpiredPopup, setShowTokenExpiredPopup] = useState(false);

  const isLogout =
    window.location && (window.location.pathname || '').toLowerCase().includes('logout');

  const isDev =
    process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT.toLowerCase() === 'dev';

  let userActivityTimeout, redirectTimeout, userEngagementTimeout;

  const logInUserDetails = JSON.parse(localStorage.getItem('userDetails'));
  const universalDashboardData = useSelector((state) => state?.universalDashboardReducer);
  const [logoutDashboardEventRegistered, setLogoutDashboardEventRegistered] = useState(false);
  const dispatch = useDispatch();
  const accessData = useSelector((state) => state?.commonRd);
  useEffect(() => {
    if (accessData?.isAccessDenied) {
      setShowTokenExpiredPopup(true);
    }
  }, [accessData]);

  useEffect(() => {
    return () => {
      setShowTokenExpiredPopup(false);
    };
  }, []);

  useEffect(() => {
    document.title = 'TAP';

    if (window.location && (window.location.search || '').toLowerCase().includes('logout=true')) {
      setTimeout(() => {
        setShowTokenExpiredPopup(false);
        //way to logout via query string
        instance.logout();
      }, NUMBER_ARRAY.sixThousand);
    }
  });

  useEffect(() => {
    if (!isAuthenticated) {
      if (isLogout) {
        //does not execute login redirect
      } else {
        setTimeout(() => {
          if (instance.getAllAccounts().length === 0) {
            instance
              .handleRedirectPromise()
              .then((_response) => {
                logError('response', _response);
              })
              .catch((err) => {
                logError('handle Redirect Promise not found', err);
              });

            if (!localStorage['redirectURI']) {
              localStorage['redirectURI'] = window.location.pathname;
            }
            instance.loginRedirect(loginRequest).catch((e) => {
              logError('login Redirect error found', e);
            });
          }
        }, AUTHENTICATION_TIMEOUT);
      }
    } else {
      instance.setActiveAccount(instance?.getAllAccounts()[0]);
      const userName = instance?.getAllAccounts()[0]?.username;
      window.msalInstance = instance;
      window.msalUserName = userName;

      if (localStorage['redirectURI'] && localStorage['redirectURI'] !== '') {
        let redirectURI = localStorage['redirectURI'];
        localStorage.removeItem('redirectURI');
        window.location.pathname = redirectURI;
      }

      // Set auto token renewal call on each 5 min
      setInterval(() => {
        let config = {
          scopes: loginRequest.scopes,
          authority: msalConfig.auth.authority,
          account: instance.getAllAccounts()[0],
        };
        let redRequest = {
          scopes: loginRequest.scopes,
          loginHint: userName,
        };
        instance
          .acquireTokenSilent(config)
          .then(
            (res) => {
              logError('Token Renewed...', isDev ? res : {});
            },
            (err) => {
              setShowTokenExpiredPopup(true);
              setTimeout(() => {
                setShowTokenExpiredPopup(false);
                if (err instanceof InteractionRequiredAuthError) {
                  return instance.acquireTokenRedirect(redRequest);
                }
              }, NUMBER_ARRAY.sixThousand);
            },
          )
          .catch((error) => {
            setShowTokenExpiredPopup(true);
            setTimeout(() => {
              setShowTokenExpiredPopup(false);
              if (error instanceof InteractionRequiredAuthError) {
                return instance.acquireTokenRedirect(redRequest);
              }
            }, NUMBER_ARRAY.sixThousand);
          });
      }, TOKEN_RENEWAL_TIME);

      setTimeout(() => {
        window.location.hash = '';
      }, LOGIN_TIMEOUT);
    }
    return () => {
      setShowTokenExpiredPopup(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    trackUserActivity();
    // empty dependency array- for the use effect to be triggered in every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackUserActivity = () => {
    window.addEventListener('load', resetTimer, true);
    window.addEventListener('mousemove', resetTimer, true);
    window.addEventListener('mousedown', resetTimer, true);
    window.addEventListener('touchstart', resetTimer, true);
    window.addEventListener('touchmove', resetTimer, true);
    window.addEventListener('click', resetTimer, true);
    window.addEventListener('keydown', resetTimer, true);
    window.addEventListener('scroll', resetTimer, true);
    window.addEventListener('wheel', resetTimer, true);
  };

  const removeEventListeners = () => {
    window.removeEventListener('load', resetTimer, true);
    window.removeEventListener('mousemove', resetTimer, true);
    window.removeEventListener('mousedown', resetTimer, true);
    window.removeEventListener('touchstart', resetTimer, true);
    window.removeEventListener('touchmove', resetTimer, true);
    window.removeEventListener('click', resetTimer, true);
    window.removeEventListener('keydown', resetTimer, true);
    window.removeEventListener('scroll', resetTimer, true);
    window.removeEventListener('wheel', resetTimer, true);
  };

  // After one minute of showing inactivity popup- logout user
  const handleUserInactivity = () => {
    setShowInactivityPopup(true);
    // Logout after 1 min of showing pop-up
    redirectTimeout = setTimeout(() => {
      recordPageExitEvent();
    }, 60000);
  };

  useEffect(() => {
    if (
      logoutDashboardEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(
        universalDashboardData?.isUniversalDashboardDataTransfered,
      )
    ) {
      setLogoutDashboardEventRegistered(false);
      handleLogout();
    }
  }, [logoutDashboardEventRegistered, universalDashboardData?.isUniversalDashboardDataTransfered]);

  // Logout user
  const handleLogout = () => {
    localStorage.clear();
    // Clear all event listeners
    removeEventListeners();
    instance.logout();
  };

  const [pageVisitedDashboardEventRegistered, setPageVisitedDashboardEventRegistered] =
    useState(false);
  useEffect(() => {
    if (
      pageVisitedDashboardEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(
        universalDashboardData?.isUniversalDashboardDataTransfered,
      )
    ) {
      setPageVisitedDashboardEventRegistered(false);
      localStorage.setItem(
        'userDetails',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('userDetails')),
          visitStartTime: universalDashboardData?.apiResponse?.data?.data?.visitStartTime,
          visitEndTime: null,
          id: universalDashboardData?.apiResponse?.data?.data?.id,
          reActivityEvent: DASHBOARD_DATA_ACCEPTABLE_STATUSES[NUMBER_ARRAY.zero],
        }),
      );
    }
  }, [
    pageVisitedDashboardEventRegistered,
    universalDashboardData?.isUniversalDashboardDataTransfered,
  ]);

  const [pageExitDashboardEventRegistered, setPageExitDashboardEventRegistered] = useState(false);
  const [
    pageInactivityExitDashboardEventRegistered,
    setPageInactivityExitDashboardEventRegistered,
  ] = useState(false);
  const recordInactivityPageExitEvent = () => {
    const currLogInUserDetails = JSON.parse(localStorage.getItem('userDetails'));
    if (!currLogInUserDetails?.visitEndTime) {
      setPageInactivityExitDashboardEventRegistered(true);
      dispatch(
        TransferUniversalDashboardData({
          apiType: 'page_exit',
          apiSubType: '',
          userEmailId: currLogInUserDetails?.user_email ?? null,
          pageTitle: currLogInUserDetails?.pageTitle ?? null,
          pageSubTitle: currLogInUserDetails?.pageSubTitle ?? null,
          pageURL: window.location.href,
          appName: process.env.REACT_APP_NAME,
          targetUrl: 'pagevisit/entry',
          idSession: currLogInUserDetails?.idSession ?? null,
          browser: getUserAgentBrowser(),
          os: getUserAgentOS(),
          visitStartTime: currLogInUserDetails?.visitStartTime ?? null,
          visitEndTime: new Date().toISOString('en-US', { timeZone: 'UTC' }),
          id: currLogInUserDetails?.id ?? null,
        }),
      );
    }
  };

  const recordPageExitEvent = () => {
    const currLogInUserDetails = JSON.parse(localStorage.getItem('userDetails'));
    setPageExitDashboardEventRegistered(true);
    dispatch(
      TransferUniversalDashboardData({
        apiType: 'page_exit',
        apiSubType: '',
        userEmailId: currLogInUserDetails?.user_email ?? null,
        pageTitle: currLogInUserDetails?.pageTitle ?? null,
        pageSubTitle: currLogInUserDetails?.pageSubTitle ?? null,
        pageURL: window.location.href,
        appName: process.env.REACT_APP_NAME,
        targetUrl: 'pagevisit/entry',
        ipAddress: null,
        idSession: currLogInUserDetails?.idSession ?? null,
        browser: getUserAgentBrowser(),
        os: getUserAgentOS(),
        visitStartTime: currLogInUserDetails?.visitStartTime ?? null,
        visitEndTime: new Date().toISOString('en-US', { timeZone: 'UTC' }),
        id: currLogInUserDetails?.id ?? null,
      }),
    );
  };
  useEffect(() => {
    if (
      pageExitDashboardEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(
        universalDashboardData?.isUniversalDashboardDataTransfered,
      )
    ) {
      setPageExitDashboardEventRegistered(false);
      recordLogoutEvent();
    }
  }, [
    pageExitDashboardEventRegistered,
    universalDashboardData?.isUniversalDashboardDataTransfered,
  ]);
  useEffect(() => {
    if (
      pageInactivityExitDashboardEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(
        universalDashboardData?.isUniversalDashboardDataTransfered,
      )
    ) {
      setPageInactivityExitDashboardEventRegistered(false);
      if (universalDashboardData?.apiResponse?.data?.data?.visitEndTime) {
        localStorage.setItem(
          'userDetails',
          JSON.stringify({
            ...JSON.parse(localStorage.getItem('userDetails')),
            visitStartTime: null,
            visitEndTime: universalDashboardData?.apiResponse?.data?.data?.visitEndTime,
            id: null,
            reActivityEvent: DASHBOARD_DATA_ACCEPTABLE_STATUSES[NUMBER_ARRAY.zero],
          }),
        );
      }
    }
  }, [pageInactivityExitDashboardEventRegistered, universalDashboardData]);
  const recordLogoutEvent = () => {
    setLogoutDashboardEventRegistered(true);
    dispatch(
      TransferUniversalDashboardData({
        apiType: 'user_session_logout_idle',
        apiSubType: '',
        userEmailId: logInUserDetails?.user_email ?? null,
        appName: process.env.REACT_APP_NAME,
        targetUrl: '/session',
        startDateTime: logInUserDetails?.loggedInTime,
        endDateTime: new Date().toISOString('en-US', { timeZone: 'UTC' }),
        ipAddress: null,
        idSession: logInUserDetails?.idSession ?? null,
        browser: getUserAgentBrowser(),
        os: getUserAgentOS(),
      }),
    );
  };

  // Reset timeout in case of any activity by user
  const resetTimer = () => {
    clearTimeout(userActivityTimeout);
    clearTimeout(redirectTimeout);
    clearTimeout(userEngagementTimeout);
    // 45 mins of inactivity will trigger popup
    userActivityTimeout = setTimeout(handleUserInactivity, 2700000);
    userEngagementTimeout = setTimeout(recordInactivityPageExitEvent, NUMBER_ARRAY.thirtyThousand);
    recordPageEntryEvent();
  };

  const [pageEntryEventRegistered, setPageEntryEventRegistered] = useState(false);
  const recordPageEntryEvent = () => {
    const currLogInUserDetails = JSON.parse(localStorage.getItem('userDetails'));
    if (currLogInUserDetails?.visitEndTime && currLogInUserDetails?.reActivityEvent !== 'loading') {
      localStorage.setItem(
        'userDetails',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('userDetails')),
          reActivityEvent: 'loading',
        }),
      );
      setPageEntryEventRegistered(true);
      dispatch(
        TransferUniversalDashboardData({
          apiType: 'page_visit',
          apiSubType: '',
          userEmailId: currLogInUserDetails?.user_email,
          pageTitle: currLogInUserDetails?.pageTitle,
          pageSubTitle: currLogInUserDetails?.pageSubTitle ?? null,
          pageURL: window.location.href,
          appName: process.env.REACT_APP_NAME,
          targetUrl: 'pagevisit/entry',
          ipAddress: null,
          idSession: currLogInUserDetails?.idSession ?? null,
          browser: getUserAgentBrowser(),
          os: getUserAgentOS(),
          visitStartTime: new Date().toISOString('en-US', { timeZone: 'UTC' }),
          visitEndTime: null,
          id: null,
        }),
      );
    }
  };
  useEffect(() => {
    if (
      pageEntryEventRegistered &&
      DASHBOARD_DATA_ACCEPTABLE_STATUSES.includes(
        universalDashboardData?.isUniversalDashboardDataTransfered,
      )
    ) {
      setPageEntryEventRegistered(false);
      localStorage.setItem(
        'userDetails',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('userDetails')),
          visitStartTime: universalDashboardData?.apiResponse?.data?.data?.visitStartTime,
          visitEndTime: null,
          id: universalDashboardData?.apiResponse?.data?.data?.id,
          reActivityEvent: DASHBOARD_DATA_ACCEPTABLE_STATUSES[NUMBER_ARRAY.zero],
        }),
      );
    }
  }, [pageEntryEventRegistered, universalDashboardData?.isUniversalDashboardDataTransfered]);

  const handleClose = () => {
    setShowInactivityPopup(false);
  };

  const handleInActivePopupCloseEvent = () => {
    setShowTokenExpiredPopup(false);
  };

  return (
    <StrictMode>
      <>
        {isLogout ? (
          <LogoutView />
        ) : isAuthenticated ? (
          <>
            <ThemeProvider theme={themeSelected}>
              <CssBaseline />
              <RouterConfig />
            </ThemeProvider>
          </>
        ) : (
          <div>Please Wait...</div>
        )}
        <InActiveStatusPopup
          open={showInactivityPopup}
          handleClose={handleClose}
          msgBody={TapGlobalMessages.sessionGoingToExpireInactivity}
          title={TapGlobalMessages.userIdlePopupTitle}
          rightBtnText={TapGlobalMessages.extendButtonText}
          leftBtnText={''}
          rightBtnHandler={() => {
            // Reset the timer
            resetTimer();
            setShowInactivityPopup(false);
          }}
          leftBtnHandler={() => {}}
        />
        <InActiveStatusPopup
          open={showTokenExpiredPopup}
          handleClose={handleInActivePopupCloseEvent}
          msgBody={TapGlobalMessages.tokenExpiredText}
          title={TapGlobalMessages.tokenExpiredHeading}
          rightBtnText={''}
          leftBtnText={''}
          rightBtnHandler={() => {}}
          leftBtnHandler={() => {}}
        />
      </>
    </StrictMode>
  );
}

export default App;
